import { Country, State } from 'country-state-city';
import type { Client } from '../services/authService';

export type ClientCountryOption = { isoCode: string; name: string };
export type ClientStateOption = { isoCode: string; name: string };

export function resolveClientCountryAndState(client: Client | null | undefined): {
  country: ClientCountryOption | null;
  state: ClientStateOption | null;
} {
  const countryCode = client?.address?.country?.trim();
  if (!countryCode) {
    return { country: null, state: null };
  }

  const countries = Country.getAllCountries();
  const country =
    countries.find((c) => c.isoCode === countryCode) ??
    countries.find((c) => c.name === countryCode) ??
    null;

  const stateCode = client?.address?.state_province?.trim();
  if (!country || !stateCode) {
    return { country, state: null };
  }

  const states = State.getStatesOfCountry(country.isoCode);
  const state =
    states.find((s) => s.isoCode === stateCode) ??
    states.find((s) => s.name === stateCode) ??
    null;

  return { country, state };
}

export function formatClientCountry(countryCode?: string): string | null {
  if (!countryCode?.trim()) return null;
  const trimmed = countryCode.trim();
  const match =
    Country.getAllCountries().find((c) => c.isoCode === trimmed) ??
    Country.getAllCountries().find((c) => c.name === trimmed);
  return match?.name ?? trimmed;
}

export function formatClientState(countryCode?: string, stateCode?: string): string | null {
  if (!stateCode?.trim()) return null;
  const trimmedState = stateCode.trim();
  const trimmedCountry = countryCode?.trim();

  if (trimmedCountry) {
    const match =
      State.getStatesOfCountry(trimmedCountry).find((s) => s.isoCode === trimmedState) ??
      State.getStatesOfCountry(trimmedCountry).find((s) => s.name === trimmedState);
    if (match) return match.name;
  }

  return trimmedState;
}

export function formatClientPhone(
  phone?: string,
  countryCode = '1',
  addressCountryIso?: string
): string | null {
  if (!phone?.trim()) return null;
  const digits = phone.replace(/\D/g, '');
  if (!digits) return null;

  if (addressCountryIso) {
    const countryMeta = Country.getCountryByCode(addressCountryIso);
    const dial = countryMeta?.phonecode;
    if (dial && digits.startsWith(dial) && digits.length > dial.length + 4) {
      const national = digits.slice(dial.length);
      return `+${dial} ${national}`;
    }
  }

  if (digits.length === 10) {
    return `+${countryCode} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return phone.startsWith('+') ? phone : `+${digits}`;
}

export function getClientLocationLine(client: Client | null | undefined): string | null {
  if (!client?.address) return null;
  const country = formatClientCountry(client.address.country);
  const state = formatClientState(client.address.country, client.address.state_province);
  const parts = [state, country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}
